import {
  AutoAwesomeOutlined,
  EditNoteOutlined,
  ViewColumnOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import Button from "@mui/material/Button";
import { isEmpty } from "lodash";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import {
  Box,
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { selectOptionsExceptionStatus } from "../../../../constants/selectOptions/selectOptionsExceptionStatus";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import Flx from "../../../../components/layout/Flx";
import LoanExceptionsTable from "../../../../components/common/Loan/tables/LoanExceptionsTable";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import AiFindingsPanel from "../../components/AiFindingsPanel";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import {
  AI_SUGGESTION_SCOPES,
  useAiSuggestionIgnored,
} from "../../../../utils/storage/aiSuggestionVisibility";

const LoanDrilldownExceptionsTab = ({ value, tab }) => {
  const [quickFilter] = useState(null);
  // Held here so the toolbar's Edit Columns button can drive the grid's
  // columns tool panel — the columns folded into Classification / People /
  // Dates are hidden, not removed, and this is how they come back.
  const gridRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const { isAdmin } = usePermissionsHook();
  // Dismissing the AI panel is a per-user, cross-loan preference; the AI Review
  // tab reads the same flag and offers the way back.
  const { ignored: aiIgnored, ignore: handleIgnoreAi } = useAiSuggestionIgnored(
    AI_SUGGESTION_SCOPES.EXCEPTIONS,
  );
  const loanExceptions = useSelector(
    (state) => state?.loanDrilldown?.exceptions || [],
  );
  const dispatch = useDispatch();

  const handleOnRowClicked = useCallback((d) => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
    dispatch(sidebarSetStateValue(d));
  }, []);

  // "All" plus every status actually present, in the dropdown's own order so
  // the row reads the same way the status field does.
  const statusFilters = useMemo(() => {
    const counts = loanExceptions.reduce((acc, e) => {
      const key = e?.exceptionStatus || "Open";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const known = selectOptionsExceptionStatus.filter((st) => counts[st]);
    const extra = Object.keys(counts).filter(
      (st) => !selectOptionsExceptionStatus.includes(st),
    );
    return [
      {
        key: ALL_STATUSES,
        label: "All Exceptions",
        count: loanExceptions.length,
      },
      ...[...known, ...extra].map((st) => ({
        key: st,
        label: st,
        count: counts[st],
      })),
    ];
  }, [loanExceptions]);

  const filteredExceptions = useMemo(
    () =>
      statusFilter === ALL_STATUSES
        ? loanExceptions
        : loanExceptions.filter(
            (e) => (e?.exceptionStatus || "Open") === statusFilter,
          ),
    [loanExceptions, statusFilter],
  );

  const aiPanel =
    isAdmin && !aiIgnored ? (
      <AiFindingsPanel
        scope="hard"
        warningTint
        hideWhenEmpty
        hideIcon
        adminChip
        rootSx={{ mb: 2.5 }}
        defaultExpanded={false}
        title="AI Suggested Exceptions"
        subtitle="Hard exceptions the AI review staged from this loan's documents — not yet on the loan. Promote to file one, edit and promote, or reject."
        onIgnore={handleIgnoreAi}
        ignoreTooltip="Hide AI suggested exceptions on every loan. Bring it back from AI Review → Suggested Hard Exceptions."
      />
    ) : null;

  const hasExceptions = !isEmpty(loanExceptions);

  return (
    <PrimaryContentCard
      title="Loan Exceptions"
      endContent={
        <Flx ac g={1}>
          <AddExceptionButton />
        </Flx>
      }
    >
      <Divider sx={{ mt: 0.5, mb: 3, borderWidth: "1.5px" }} />
      {hasExceptions && (
        <Flx ac wrap sx={{ gap: "8px", mb: 2 }}>
          {statusFilters.map((f) => (
            <StatusFilterChip
              key={f.key}
              label={f.label}
              count={f.count}
              active={statusFilter === f.key}
              onClick={() => setStatusFilter(f.key)}
            />
          ))}
          <Box sx={{ ml: "auto" }}>
            <EditColumnsButton gridRef={gridRef} />
          </Box>
        </Flx>
      )}
      {aiPanel}
      {hasExceptions ? (
        <LoanExceptionsTable
          ref={gridRef}
          rowData={filteredExceptions}
          onRowClicked={handleOnRowClicked}
          quickFilterText={quickFilter}
        />
      ) : (
        <EmptyStatePlaceholder
          icon={<WarningAmberOutlined />}
          title="No exceptions on this loan"
          description="Log any hard or soft exceptions with compensating factors and a resolution plan related to this loan."
          actions={
            <Flx ac g={1}>
              <AddExceptionButton />
            </Flx>
          }
        />
      )}
    </PrimaryContentCard>
  );
};

const ALL_STATUSES = "__all__";

const CHIP_INK = "#0f172a";
const CHIP_INK_2 = "#334155";
const CHIP_BORDER = "#e2e8f0";
const CHIP_MUTED = "#94a3b8";

const StatusFilterChip = ({ label, count, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "7px",
      cursor: "pointer",
      font: "inherit",
      fontSize: "12px",
      fontWeight: active ? 600 : 500,
      padding: "6px 15px",
      borderRadius: "99px",
      whiteSpace: "nowrap",
      color: active ? "#fff" : CHIP_INK_2,
      bgcolor: active ? CHIP_INK : "#fff",
      border: `1px solid ${active ? CHIP_INK : CHIP_BORDER}`,
      "&:hover": { borderColor: active ? CHIP_INK : CHIP_MUTED },
    }}
  >
    {label}
    <Box component="span" sx={{ fontSize: "10.2px", opacity: 0.7 }}>
      {count}
    </Box>
  </Box>
);

/**
 * Column visibility menu. AG Grid's columns tool panel needs a sized grid
 * container and does nothing under `domLayout="autoHeight"`, so this drives
 * `setColumnsVisible` directly — which is also what the "bring back the
 * columns folded into Classification / People / Dates" ask actually needs.
 */
const EditColumnsButton = memo(({ gridRef }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [columns, setColumns] = useState([]);

  const readColumns = useCallback(() => {
    const api = gridRef?.current?.api;
    if (!api) return [];
    return (
      (api.getColumns() || [])
        .map((col) => ({
          colId: col.getColId(),
          headerName: col.getColDef()?.headerName,
          visible: col.isVisible(),
        }))
        // Skip the id-only helper columns that have no header to show.
        .filter((c) => !!c.headerName)
    );
  }, [gridRef]);

  const handleOpen = (e) => {
    setColumns(readColumns());
    setAnchorEl(e.currentTarget);
  };

  const handleToggle = (colId, visible) => {
    const api = gridRef?.current?.api;
    if (!api) return;
    api.setColumnsVisible([colId], !visible);
    setColumns(readColumns());
  };

  return (
    <>
      <Button
        variant={anchorEl ? "outlined" : "text"}
        startIcon={<ViewColumnOutlined sx={{ fontSize: 16 }} />}
        onClick={handleOpen}
      >
        Edit Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 240 } } }}
      >
        {columns.map((c) => (
          <MenuItem
            key={c.colId}
            dense
            onClick={() => handleToggle(c.colId, c.visible)}
          >
            <Checkbox size="small" checked={c.visible} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText
              primary={c.headerName}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

const AddExceptionButton = memo(() => {
  const dispatch = useDispatch();

  const handleToggle = () => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
    dispatch(sidebarSetStateValue(null));
  };

  return (
    <Button startIcon={<EditNoteOutlined />} onClick={handleToggle}>
      Add Exception
    </Button>
  );
});

const ViewRedesignButton = memo(() => {
  const navigate = useNavigate();
  return (
    <Button
      variant="outlined"
      startIcon={<AutoAwesomeOutlined sx={{ fontSize: 16 }} />}
      onClick={() => navigate("../exceptions-redesign")}
      sx={{ textTransform: "none", fontWeight: 600 }}
    >
      View Redesign
    </Button>
  );
});

export default LoanDrilldownExceptionsTab;
